import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { shouldShowFindingFilmmaker } from '@/utils/booking-steps';
import { invokeFunctionWithAuth } from '@/lib/supabase/functions';
import { toast } from 'sonner';

/**
 * Smart router component that redirects to the appropriate tracking page
 * based on the booking's current status
 */
export default function TrackingRouter() {
  const navigate = useNavigate();
  const { bookingId } = useParams<{ bookingId: string }>();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const determineRoute = async () => {
      if (!bookingId) {
        console.error('No booking ID provided');
        navigate('/dashboard');
        return;
      }

      try {
        // Fetch booking from database
        const userId = user?.id || localStorage.getItem('auth_session')?.split('"userId":"')[1]?.split('"')[0];
        
        if (!userId) {
          toast.error('You must be logged in to view this booking');
          navigate('/auth/login');
          return;
        }

        const { data: bookingResponse, error } = await invokeFunctionWithAuth('get-booking-details', {
          bookingId: bookingId,
          userId: userId,
          phone: user?.phone
        });

        if (error || !bookingResponse?.booking) {
          console.error('Error fetching booking:', error);
          toast.error('Booking not found');
          navigate('/dashboard');
          return;
        }

        const booking = bookingResponse.booking;

        if (!booking) {
          toast.error('Booking not found');
          navigate('/dashboard');
          return;
        }

        // Redirect based on status
        if (shouldShowFindingFilmmaker(booking.status)) {
          console.log('📍 Redirecting to FindingFilmmaker page');
          navigate(`/track/${bookingId}/finding`, { replace: true });
        } else {
          console.log('📍 Redirecting to TrackBooking page');
          navigate(`/track/${bookingId}/details`, { replace: true });
        }
      } catch (error) {
        console.error('Error determining route:', error);
        toast.error('Failed to load booking');
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    determineRoute();
  }, [bookingId, user, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading booking...</p>
        </div>
      </div>
    );
  }

  return null;
}

